/**
 * Background subscriber for CRM ticket events via `ws/crm/`.
 *
 * Listens for `crm.event` payloads pushed from the Django consumer and:
 *   - shows a sonner toast with the event kind + ticket id,
 *   - invalidates the relevant React Query caches so the next view pulls
 *     fresh data immediately.
 *
 * Conversation updates do NOT arrive here: the omnichannel CRM lives on the
 * fasto_bi service (no WebSocket source) and the conversation pages poll
 * every 10–15s instead.
 *
 * Mounted once inside `AppShell`, so the connection lives across all
 * authenticated routes — no per-page wiring.
 */
import { useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useWebSocket } from '@/api/ws';
import { notify } from '@/lib/toast';

interface CrmEventPayload {
  ticket_id?: number;
  type?: string;
  status?: string;
  priority?: string;
  summary?: string;
  order_id?: number | null;
  store_id?: number | null;
  decision?: string;
  refund_id?: number;
  shrinkage_id?: number;
}

type CrmEventKind =
  | 'assigned'
  | 'escalated'
  | 'auto_created'
  | 'resolved'
  | 'decision'
  | 'tier_advanced'
  | 'ack_overdue'
  | 'response_breached'
  | 'resolution_breached';

interface CrmEventMessage {
  type: 'crm.event' | 'crm.connected';
  kind?: CrmEventKind;
  payload?: CrmEventPayload;
}

const KIND_TITLE: Record<string, string> = {
  assigned: 'Assigned to you',
  escalated: 'Ticket escalated',
  auto_created: 'New ticket auto-created',
  resolved: 'Ticket resolved',
  decision: 'Refund decision',
  tier_advanced: 'Escalation tier advanced',
  ack_overdue: 'Acknowledgement overdue',
  response_breached: 'Response SLA breached',
  resolution_breached: 'Resolution SLA breached',
};

const WARNING_KINDS: ReadonlySet<string> = new Set([
  'escalated',
  'tier_advanced',
  'ack_overdue',
  'response_breached',
  'resolution_breached',
]);

export function CrmEventsSubscriber() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const lastEventId = useRef<string | null>(null);

  useWebSocket<CrmEventMessage>({
    path: '/ws/crm/',
    onMessage: (msg) => {
      if (!msg || msg.type !== 'crm.event' || !msg.kind || !msg.payload) return;
      const id = msg.payload.ticket_id;
      const fingerprint = `${msg.kind}:${id}:${msg.payload.status ?? ''}`;
      if (lastEventId.current === fingerprint) return;
      lastEventId.current = fingerprint;

      const title = KIND_TITLE[msg.kind] ?? 'CRM update';
      const summary = msg.payload.summary || msg.payload.type || `Ticket #${id}`;
      const toaster = WARNING_KINDS.has(msg.kind) ? notify.warning : notify.success;
      toaster(`${title}${id != null ? `: #${id}` : ''}`, {
        description: summary,
        action: {
          label: 'Open',
          onClick: () => navigate(`/crm/tickets/${id}`),
        },
      });

      void qc.invalidateQueries({ queryKey: ['bi', 'crm'] });
    },
  });

  return null;
}

export default CrmEventsSubscriber;
